import { useEffect, useState } from "react";
import { Loader2, Image as ImageIcon } from "lucide-react";
import { API, adminImageCache } from "./admin-utils";

export function AdminItemImage({ itemId, itemName }) {
  const [imageUrl, setImageUrl] = useState(() => adminImageCache.get(itemId) || null);
  const [loading, setLoading] = useState(!imageUrl);
  // Bumps whenever this item's image is invalidated → forces re-fetch
  const [version, setVersion] = useState(0);

  // Listen for external invalidations (e.g. after saving a new image in the admin modal)
  useEffect(() => {
    const handler = (e) => {
      if (e?.detail?.itemId === itemId) {
        setImageUrl(null);
        setLoading(true);
        setVersion((v) => v + 1);
      }
    };
    window.addEventListener("menu-image-updated", handler);
    return () => window.removeEventListener("menu-image-updated", handler);
  }, [itemId]);

  useEffect(() => {
    // Serve from cache when available (unless we just invalidated)
    if (imageUrl) {
      setLoading(false);
      return;
    }
    if (version === 0 && adminImageCache.has(itemId)) {
      setImageUrl(adminImageCache.get(itemId));
      setLoading(false);
      return;
    }

    let cancelled = false;
    fetch(`${API}/menu/${itemId}/image`)
      .then((res) => res.json())
      .then((data) => {
        if (!cancelled && data.image_base64) {
          adminImageCache.set(itemId, data.image_base64);
          setImageUrl(data.image_base64);
        }
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [itemId, imageUrl, version]);

  if (loading) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-gray-100">
        <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
      </div>
    );
  }

  if (imageUrl) {
    return <img src={imageUrl} alt={itemName} className="w-full h-full object-cover" />;
  }

  return (
    <div className="w-full h-full flex items-center justify-center text-gray-300">
      <ImageIcon className="w-6 h-6" />
    </div>
  );
}
